import React, { Suspense, useEffect, useState, useRef, useMemo } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { Html, OrbitControls, Environment, Float, Text3D, Center, useTexture, PerspectiveCamera } from '@react-three/drei';
import styled, { keyframes, createGlobalStyle, css } from 'styled-components';
import * as THREE from 'three';

// Palette de couleurs pour le terminal
const colors = {
  background: '#0a0e17',
  primary: '#39ff14',    // Vert néon
  secondary: '#00b3ff',  // Bleu néon
  accent: '#ff00ff',     // Rose néon
  text: '#ffffff',
  darkGlass: 'rgba(10, 14, 23, 0.85)',
  terminalBg: '#0c0c0c',
  terminalBorder: '#39ff14'
};

// Overlay pour centrer le terminal au-dessus du fond 3D

const WidgetToggleButton = styled.button`
  position: absolute;
  top: -35px;
  right: 0;
  background: transparent;
  border: 1px solid ${colors.primary};
  color: ${colors.primary};
  width: 30px;
  height: 30px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  line-height: 1;
  padding-bottom: 4px;
  text-shadow: 0 0 5px ${colors.primary};
  transition: all 0.2s ease-in-out;

  &:hover {
    background: ${colors.primary};
    color: #000;
    text-shadow: none;
    transform: scale(1.1);
  }
`;

const CodeurWidgetContainer = styled.div`
  position: fixed;
  bottom: 10px;
  right: 20px;
  z-index: 2000;
  transform: translateY(${props => props.visible ? '0' : '150%'});
  opacity: ${props => props.visible ? 1 : 0};
  transition: transform 0.5s ease-in-out, opacity 0.5s ease-in-out;
  pointer-events: ${props => props.visible ? 'auto' : 'none'};

  @media (max-width: 768px) {
    bottom: 5px;
    right: 5px;
  }
`;

const TerminalOverlay = styled.div`
  position: fixed;
  top: 2rem; /* Adjusted to give space at the top */
  left: 50%; /* Center horizontally */
  transform: translateX(-50%); /* Center horizontally */
  width: 100%;
  max-width: 90%; /* Adjusted for better responsiveness */
  max-height: 95vh; /* Adjusted for better responsiveness */

  z-index: 100;
  pointer-events: none;

  & > * {
    pointer-events: auto;
    width: 100%;
  }
`;

// Animations
const blink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
`;

const glitch = keyframes`
  0% {
    text-shadow: 0.05em 0 0 rgba(255, 0, 0, 0.75),
                 -0.05em -0.025em 0 rgba(0, 255, 0, 0.75);
  }
  14% {
    text-shadow: 0.05em 0 0 rgba(255, 0, 0, 0.75),
                 -0.05em -0.025em 0 rgba(0, 255, 0, 0.75);
  }
  15% {
    text-shadow: -0.05em -0.025em 0 rgba(255, 0, 0, 0.75),
                 0.025em 0.025em 0 rgba(0, 0, 255, 0.75);
  }
  49% {
    text-shadow: -0.05em -0.025em 0 rgba(255, 0, 0, 0.75),
                 0.025em 0.025em 0 rgba(0, 0, 255, 0.75);
  }
  50% {
    text-shadow: 0.025em 0.05em 0 rgba(0, 255, 0, 0.75),
                 0.05em 0 0 rgba(255, 0, 0, 0.75);
  }
  99% {
    text-shadow: 0.025em 0.05em 0 rgba(0, 255, 0, 0.75),
                 0.05em 0 0 rgba(255, 0, 0, 0.75);
  }
  100% {
    text-shadow: -0.025em 0 0 rgba(0, 0, 255, 0.75),
                 -0.025em -0.025em 0 rgba(255, 0, 0, 0.75);
  }
`;

const scanlineSweep = keyframes`
  0% {
    transform: translateY(-10%);
  }
  100% {
    transform: translateY(110%);
  }
`;





// Composant pour l'effet de frappe
const TypingEffect = ({ text, speed = 50 }) => {
  const [displayedText, setDisplayedText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    setDisplayedText('');
    setCurrentIndex(0);
  }, [text]);

  useEffect(() => {
    if (currentIndex < text.length) {
      const timeoutId = setTimeout(() => {
        setDisplayedText((prev) => prev + text[currentIndex]);
        setCurrentIndex((prev) => prev + 1);
      }, speed);
      return () => clearTimeout(timeoutId);
    }
  }, [currentIndex, text, speed]);

  return <>{displayedText}</>;
};

const BlinkingPrompt = styled.span`
  animation: ${blink} 1.2s step-end infinite;
  margin-right: 0.5ch; // Espace entre le curseur et le texte

  @media (max-width: 600px) {
    margin-right: 0.3ch;
  }
`;

const SectionTitle = styled.h3`
  margin-top: 3rem;
  margin-bottom: 2rem;
  color: ${colors.primary};
  text-shadow: 0 0 10px rgba(57, 255, 20, 0.4);
  transition: all 0.3s ease;
  position: relative;
  display: block;
  word-break: break-word;
  overflow-wrap: break-word;
  white-space: normal;
  max-width: 100%;
  
  &:hover {
    text-shadow: 0 0 5px ${colors.primary}, 0 0 10px ${colors.primary};
  }

  @media (max-width: 600px) {
    font-size: 0.95em;
    margin-top: 1rem;
    margin-bottom: 0.8rem;
    width: 100%;
    max-width: 98vw;
    padding-right: 2vw;
  }
`;

// Données des projets avec images
const projects = [
  {
    id: 1,
    title: 'SOCIETE MARAVAL',
    url: 'https://www.societemaraval.com',
    description: 'Site vitrine pour une entreprise de construction',
    longDescription: 'Développement d\'un site vitrine pour une entreprise de construction. Le site présente les services de l\'entreprise, ses réalisations et ses coordonnées.',
    imageUrl: '/SOCIETE MARAVAL | MENUISERIE | PVC | ALU.png',
    link: 'https://www.societemaraval.com',
    year: '2022',
    technologies: ['Wordpress']
  },
  {
    id: 2,
    title: 'STUDIO 24 PILATES',
    url: 'https://www.studio24pilates.fr',
    description: 'Site vitrine pour un studio de pilates',
    longDescription: 'Développement d\'un site vitrine pour un studio de pilates. Le site présente les services du studio, les horaires des cours et les informations de contact.',
    imageUrl: '/studio24.jpg',
    link: 'https://www.studio24pilates.fr',
    year: '2022',
    technologies: ['WebFlow']
  },
  {
    id: 3,
    title: 'MARBRERIE PISICCHIO',
    url: 'https://www.marbrerie-pisicchio.fr',
    description: 'Site vitrine pour une marbrerie',
    longDescription: 'Développement d\'un site vitrine pour une marbrerie. Le site présente les services de l\'entreprise, ses réalisations et ses coordonnées.',
    imageUrl: '/marbrerie.jpg',
    link: 'https://www.marbrerie-pisicchio.fr',
    year: '2022',
    technologies: ['Wordpress']
  },
  {
    id: 4,
    title: 'BAZAR PALMERAIE',
    url: 'https://www.bazarmarseille.com',
    description: 'Site pour une discotheque',
    longDescription: 'Développement d\'un site pour une discotheque. Le site présente les événements et les coordonnées de la discotheque.',
    imageUrl: '/bazar.jpg',
    link: 'https://www.bazarmarseille.com',
    year: '2022',
    technologies: ['Wordpress']
  },
  {
    id: 5,
    title: 'AE BEAUTY SPA',
    url: 'https://aebeautyspa.fr',
    description: 'Site de réservation pour un spa',
    longDescription: 'Développement d\'un site de réservation pour un spa. Le site permet aux utilisateurs de réserver des soins et de gérer leurs comptes.',
    imageUrl: '/aebeauty.jpg',
    link: 'https://aebeautyspa.fr',
    year: '2022',
    technologies: ['Wordpress']
  },

  {
    id: 7,
    title: 'DAVID BRASSERIE',
    url: 'https://www.ledavidmarseille.fr',
    description: 'Site vitrine pour un restaurant',
    longDescription: 'Développement d\'un site vitrine pour un restaurant. Le site présente le menu, les informations de contact et les horaires d\'ouverture.',
    imageUrl: '/david.jpg',
    link: 'https://www.ledavidmarseille.fr',
    year: '2022',
    technologies: ['React']
  }
];

const projectsApp = [
  {
    id: 101,
    title: 'PALMERAIE',
    description: 'Application iOS/Android pour la communauté Palmeraie/Bazar Marseille.',
    longDescription: "Développement d'une application mobile iOS pour la discothèque Bazar Palmeraie. L'application permet aux utilisateurs de consulter l'agenda des événements, de réserver des tables ou des entrées, de recevoir des notifications sur les soirées spéciales et de découvrir les DJs invités. Elle vise à enrichir l'expérience client et à faciliter l'accès aux informations du club.",
    imageUrl: '/bazar.jpg',
    link: 'https://apps.apple.com/fr/app/palmeraie/id6444081222',
    year: '2023',
    technologies: ['Flutter']
  }
];

// Styles globaux pour l'effet terminal authentique
const GlobalTerminalEffects = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;700&display=swap');
  
  /* Animation de glow pour le terminal */
  @keyframes terminalGlow {
    0% {
      box-shadow: 0 0 10px rgba(57, 255, 20, 0.3);
      border-color: rgba(57, 255, 20, 0.7);
    }
    50% {
      box-shadow: 0 0 20px rgba(57, 255, 20, 0.5), 0 0 30px rgba(57, 255, 20, 0.2);
      border-color: rgba(57, 255, 20, 0.9);
    }
    100% {
      box-shadow: 0 0 15px rgba(57, 255, 20, 0.3);
      border-color: rgba(57, 255, 20, 0.8);
    }
  }
  
  /* Effet de bruit sur l'arrière-plan */
  body::before {
    content: "";
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E");
    opacity: 0.05;
    z-index: 1;
    animation: noise 0.5s steps(1) infinite;
  }
  
  @keyframes noise {
    0%, 100% { opacity: 0.05; }
    10% { opacity: 0.07; }
    20% { opacity: 0.04; }
    30% { opacity: 0.06; }
    40% { opacity: 0.03; }
    50% { opacity: 0.05; }
    60% { opacity: 0.04; }
    70% { opacity: 0.07; }
    80% { opacity: 0.03; }
    90% { opacity: 0.06; }
  }
  
  body {
    margin: 0;
    padding: 0;
    overflow: hidden;
    overflow-x: hidden;
    background-color: #000000;
    color: ${colors.text};
    font-family: 'JetBrains Mono', monospace;
    width: 100%;
    font-size: 14px;
    line-height: 1.6;
  }
  
  .visible {
    opacity: 1;
    transform: translateY(0);
  }
  
  #root {
    width: 100vw;
    height: 100vh;
    margin: 0;
    padding: 0;
    overflow: hidden;
    overflow-x: hidden;
  }
  
  .noise-bg {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    background-image: url('https://media.giphy.com/media/oEI9uBYSzLpBK/giphy.gif');
    opacity: 0.02;
    pointer-events: none;
    z-index: 5;
  }
  
  .scanline {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0) 0%,
      rgba(0, 0, 0, 0.2) 50%,
      rgba(0, 0, 0, 0) 100%
    );
    background-size: 100% 4px;
    pointer-events: none;
    z-index: 10;
    animation: scanline 6s linear infinite;
  }
  
  @keyframes scanline {
    0% {
      background-position: 0 0;
    }
    100% {
      background-position: 0 100%;
    }
  }
  
  .glitch {
    position: relative;
    color: ${colors.primary};
    text-shadow: 0 0 10px ${colors.primary};
  }
  
  .cursor {
    display: inline-block;
    width: 10px;
    height: 1.2rem;
    background: ${colors.primary};
    margin-left: 5px;
    animation: blink 1s infinite;
  }
  
  @keyframes blink {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0;
    }
  }
  
  
  .terminal-link {
    color: ${colors.primary};
    text-decoration: underline;
    transition: all 0.3s ease;
  }
  
  .terminal-link:hover {
    color: ${colors.secondary};
    text-shadow: 0 0 8px ${colors.secondary};
  }
  
  .noise-bg {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background-image: url('https://media.giphy.com/media/oEI9uBYSzLpBK/giphy.gif');
    background-size: cover;
    pointer-events: none;
    z-index: 0;
    opacity: 0.04;
    mix-blend-mode: screen;
  }
`;

// Conteneurs principaux
const TerminalContainer = styled.div`
  width: 100vw;
  height: 100vh;
  background: ${colors.background};
  overflow: hidden;
  position: relative;
`;

// Terminal principal avec effet de verre - version plein écran
const TerminalPanel = styled.div`
  font-family: 'JetBrains Mono', monospace;
  color: ${colors.primary};
  font-size: 1rem;
  text-shadow: 0 0 10px rgba(57, 255, 20, 0.4);
  letter-spacing: 0.05em;
  opacity: 1;
  transition: opacity 0.5s ease-in-out;
  padding: 60px 1.5rem 2.5rem 1.5rem; /* top right/left bottom - Adjusted padding-top */
  background: rgba(0, 0, 0, 0.95);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  overflow-y: auto;
  overflow-x: hidden;
  box-sizing: border-box;
  margin: 0;
  border-radius: 0;
  z-index: 10;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  border: 1px solid ${colors.primary};
  

  
  &::after {
    content: '';
    position: absolute;
    top: 9px;
    left: 12px;
  }
  
  &::before, &::after {
    pointer-events: none;
  }
  
  /* Styles des boutons supprimés */
  box-shadow: 0 0 30px rgba(57, 255, 20, 0.4), inset 0 0 15px rgba(57, 255, 20, 0.1);
  backdrop-filter: blur(5px);
  animation: terminalGlow 4s infinite alternate;
  /* padding-top: 30px; --- Consolidated above in main padding rule */
  position: relative;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 30px;
    background: rgba(20, 20, 20, 0.8);
    border-bottom: 2px solid ${colors.primary};
    border-radius: 0;
    display: flex;
    align-items: center;
    padding: 0 10px;
  }
  
  &:after {
    content: '● ● ●';
    position: absolute;
    top: 8px;
    left: 15px;
    font-size: 12px;
    color: ${colors.primary};
    text-shadow: 0 0 5px ${colors.primary};
  }
  
  &::-webkit-scrollbar {
    width: 5px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(10, 14, 23, 0.4);
    border-radius: 10px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(57, 255, 20, 0.4);
    border-radius: 10px;
  }
  
  @media (max-width: 600px) {
    font-size: 0.8rem;
    padding: 70px 1rem 2.5rem; /* top horizontal bottom - Ajusté pour un espacement modéré */
  }
`;

// Header fixe avec navigation style terminal
const Header = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  padding: 1rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 100;
  background: #000000; /* Black background */
  border-bottom: 1px solid ${colors.terminalBorder};
  
  @media (max-width: 600px) {
    padding: 0.5rem 1rem;
  }
`;

const SiteTitle = styled.div`
  color: ${colors.primary};
  font-size: 0.9rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  
  @media (max-width: 600px) {
    font-size: 0.7rem;
  }
`;

const Nav = styled.nav`
  display: flex;
  gap: 2rem;
  padding: 0.5rem 1rem;
  
  a {
    color: ${colors.text};
    text-decoration: none;
    font-size: 1rem;
    transition: all 0.3s ease;
    position: relative;
    padding: 0;
    
    &:hover, &.active {
      color: ${colors.primary};
    }
    
    &:after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      width: 0;
      height: 2px;
      background: ${colors.primary};
      transition: width 0.3s ease;
    }
    
    &:hover:after, &.active:after {
      width: 100%;
    }
    
    @media (max-width: 600px) {
      font-size: 0.8rem;
    }
  }
`;

// Grille de projets - style terminal
const ProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
  padding: 1.5rem 2rem; /* Align with SectionTitle (Desktop) */
  justify-content: center; /* Center grid tracks if they don't fill the width */

  @media (max-width: 600px) {
    padding: 1rem; /* Align with SectionTitle (Mobile) */
    gap: 1rem;
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr)); /* Smaller min for cards on mobile */
  }
  width: 100%;
  margin: 0;
  max-width: 100%;
  padding: 0 1rem;
  
  @media (max-width: 600px) {
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 1rem;
  }
`;

// Carte de projet style terminal authentique
const ProjectCard = styled.div`
  position: relative;
  transition: all 0.2s ease;
  border: 1px solid ${colors.terminalBorder};
  background: transparent;
  padding: 1.2rem;
  padding-bottom: 2.2rem; /* Espace supplémentaire pour le tag */
  cursor: pointer;
  display: flex;
  flex-direction: column;
  margin-bottom: 0.5rem;
  overflow: hidden;
  
  &:hover {
    border-color: ${colors.primary};
    box-shadow: 0 0 15px rgba(57, 255, 20, 0.5), 0 0 25px rgba(57, 255, 20, 0.2);
    transform: scale(1.02);
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 3px;
    background-color: rgba(57, 255, 20, 0.3);
    box-shadow: 0 0 8px rgba(57, 255, 20, 0.5);
    opacity: 0;
    transform: translateY(-10%);
    pointer-events: none;
    z-index: 1;
  }

  &:hover::after {
    opacity: 0.8;
    animation: ${scanlineSweep} 0.4s linear;
  }
  
  &:before {
    content: '>';
    color: ${colors.primary};
    position: absolute;
    left: 0.5rem;
    top: 1.2rem;
    font-family: monospace;
  }
  
  h4 {
    margin: 0 0 0.8rem 0;
    color: ${colors.primary};
    font-size: 1rem;
    padding-left: 1.2rem;
    font-family: 'JetBrains Mono', monospace;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  
  p {
    margin: 0;
    font-size: 0.8rem;
    color: ${colors.text};
    padding-left: 1.2rem;
    opacity: 0.7;
  }
  
  @media (max-width: 600px) {
    padding: 0.8rem;
    padding-bottom: 1.8rem;
    margin-bottom: 0.3rem;
  }
`;

// Section avec titre - style terminal
const Section = styled.section`
  margin: 0;
  width: 100%;
  padding: 0;
  
  h3 {
    color: ${colors.primary};
    margin: 0;
    font-size: 1.2rem;
    letter-spacing: 0.1em;
    font-weight: 700;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    text-transform: uppercase;
    font-family: 'JetBrains Mono', monospace;
    padding: 0;
    white-space: normal;
    word-break: break-word;
    overflow-wrap: break-word;

    &:before {
      content: '>';
      color: ${colors.primary};
      margin-right: 0.5rem;
      flex-shrink: 0;
    }
    
    &:after {
      content: '';
      flex-grow: 1;
      height: 1px;
      background: ${colors.primary};
      margin-left: 1rem;
      opacity: 0.3;
      min-width: 0;
    }
    
    @media (max-width: 600px) {
      font-size: 0.95rem;
      margin-bottom: 0.8rem;
      margin-top: 0.8rem;
      padding: 0 0.2rem;
      line-height: 1.15;
    }
  }
`;

// Liens sociaux
const SocialLinks = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 2rem;

  .terminal-link {
    color: ${colors.primary};
    text-decoration: none;
    border: 1px solid ${colors.primary};
    padding: 0.5rem 1rem;
    transition: background-color 0.3s, color 0.3s;

    &:hover {
      background-color: ${colors.primary};
      color: #000;
    }
    
    @media (max-width: 600px) {
      padding: 0.3rem 0.7rem;
      font-size: 0.8rem;
    }
  }
`;

const ScrollContainer = styled.div`
  width: 100%;
  height: 100%;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: ${colors.primary} #000000;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #000000;
  }
  
  &::-webkit-scrollbar-thumb {
    background-color: ${colors.primary};
    border-radius: 3px;
  }
`;

const TerminalFooterStyled = styled.footer`
  font-family: 'JetBrains Mono', monospace;
  color: ${colors.primary};
  font-size: 0.8rem;
  text-align: center;
  padding: 1rem 1.5rem;
  border-top: 1px dashed ${colors.terminalBorder};
  margin-top: auto; /* Pushes footer to the bottom if MainContent is flex */
  width: 100%;
  box-sizing: border-box;
  text-shadow: 0 0 5px rgba(57, 255, 20, 0.3);

  @media (max-width: 600px) {
    font-size: 0.7rem;
    padding: 0.75rem 1rem;
  }
`;

const FormWrapper = styled.div`
  max-width: 600px;
  margin: 2rem auto 0 auto; /* Added top margin */
  padding: 0 1rem; /* Added horizontal padding */
  display: flex;
  flex-direction: column;
  align-items: center; /* Center children like captcha */
  
  @media (max-width: 600px) {
    margin: 1rem auto 0 auto;
    padding: 0 0.5rem;
  }
`;

const StyledInput = styled.input`
  background: transparent;
  border: 1px solid ${colors.primary};
  padding: 0.75rem;
  color: ${colors.text};
  width: 100%;
  margin-bottom: 1.5rem;
  font-family: 'JetBrains Mono', monospace;
  box-sizing: border-box; /* Ensure padding doesn't expand width */

  &::placeholder {
    color: ${colors.secondaryText};
  }
  
  @media (max-width: 600px) {
    padding: 0.5rem;
    font-size: 0.8rem;
  }
`;

const StyledTextarea = styled.textarea`
  background: transparent;
  border: 1px solid ${colors.primary};
  padding: 0.75rem;
  color: ${colors.text};
  width: 100%;
  margin-bottom: 1.5rem;
  font-family: 'JetBrains Mono', monospace;
  resize: vertical;
  min-height: 100px; /* Added min-height */
  box-sizing: border-box; /* Ensure padding doesn't expand width */

  &::placeholder {
    color: ${colors.secondaryText};
  }
  
  @media (max-width: 600px) {
    padding: 0.5rem;
    font-size: 0.8rem;
  }
`;

const StyledButton = styled.button`
  background: transparent;
  border: 1px solid ${colors.primary};
  padding: 0.75rem 1.5rem;
  color: ${colors.primary};
  cursor: pointer;
  font-family: 'JetBrains Mono', monospace;
  transition: all 0.3s;
  width: auto; /* Changed from 100% to auto */
  align-self: flex-start; /* Align button to the left within the form */

  &:hover {
    background: ${colors.primary}20; /* Using the existing hover effect */
  }
  
  @media (max-width: 600px) {
    padding: 0.5rem 1rem;
    font-size: 0.8rem;
  }
`;

const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start; /* Sections should align to the start of MainContent */
  justify-content: flex-start;
  width: 100%; /* Occupy full width of parent (TerminalPanel), respecting its padding */
  /* height: 100vh; Removed, height will be determined by content & flex */
  /* overflow-y: auto; Removed, TerminalPanel handles scrolling */
  padding: 0;
  box-sizing: border-box;
`;

const SectionsContainer = styled.div`
  width: 100%;
  padding: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  max-width: 100%;
  margin: 0;
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000; /* S'assurer que la modale est au-dessus */
  backdrop-filter: blur(5px);
`;

// Nouvelle animation pour le défilement vertical
const scrollVertical = keyframes`
  0% {
    transform: translateY(0%);
  }
  50% {
    transform: translateY(calc(-100% + 300px)); /* 300px est la hauteur du ModalImageWrapper */
  }
  100% {
    transform: translateY(0%);
  }
`;

// Conteneur pour l'image avec défilement
const ModalImageWrapper = styled.div`
  width: 100%;
  height: 300px; /* Hauteur fixe du cadre de l'image */
  overflow-y: auto; /* Permet le défilement vertical si contenu dépasse */
  border-radius: 4px;
  margin-bottom: 1rem;
  background: #222; /* Fond si l'image est transparente ou charge */

  /* Stylisation de la barre de défilement pour Webkit (Chrome, Safari, Edge) */
  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-track {
    background: #111; /* Un fond très sombre pour la piste */
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: ${colors.primary};
    border-radius: 4px;
    border: 1px solid #0A0A0A; /* Couleur du fond de la modale */
  }
  &::-webkit-scrollbar-thumb:hover {
    background-color: ${colors.primary}; /* Peut être légèrement éclairci si besoin */
    opacity: 0.8;
  }

  /* Stylisation pour Firefox */
  scrollbar-width: thin;
  scrollbar-color: ${colors.primary} #111;
`;

const ModalContent = styled.div`
  background: #0A0A0A; /* Un peu plus clair que le fond du terminal */
  color: ${colors.primary};
  padding: 2rem;
  border: 1px solid ${colors.primary};
  border-radius: 8px;
  box-shadow: 0 0 20px rgba(57, 255, 20, 0.5);
  width: 80%;
  max-width: 600px;
  max-height: 80vh;
  overflow-y: auto;
  font-family: 'JetBrains Mono', monospace;
  position: relative; /* Pour le positionnement du bouton de fermeture */

  /* Style de la scrollbar */
  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-track {
    background: #0A0A0A;
  }
  &::-webkit-scrollbar-thumb {
    background-color: ${colors.primary};
    border-radius: 4px;
    border: 2px solid #0A0A0A;
  }
`;

const ModalHeader = styled.h2`
  color: ${colors.primary};
  font-size: 1.8em;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  text-shadow: 0 0 8px ${colors.primary};
`;

const ModalImage = styled.img`
  width: 100%;
  display: block; /* Enlève l'espace sous l'image si elle est inline */
  animation: ${scrollVertical} 80s linear infinite;

  /* Met en pause l'animation au survol */
  &:hover {
    animation-play-state: paused;
  }
`;

const ModalDescription = styled.p`
  font-size: 1rem;
  color: ${colors.primary}; /* Passage en vert néon */
  line-height: 1.6;
  margin-bottom: 1.5rem;
  white-space: pre-wrap; /* Conserve les sauts de ligne et espaces */
  text-shadow: 0 0 3px rgba(57, 255, 20, 0.3); /* Légère ombre pour le texte vert */
`;

const TerminalFooter = styled.footer`
  margin-top: 3rem;
  padding: 1.5rem 0;
  border-top: 1px solid ${colors.terminalBorder};
  text-align: center;
  color: ${colors.primary};
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.9rem;
  
  &::before {
    content: '>';
    margin-right: 0.5rem;
    color: ${colors.primary};
  }
  
  a {
    color: ${colors.primary};
    text-decoration: none;
    margin: 0 0.5rem;
    transition: text-shadow 0.3s ease;
    
    &:hover {
      text-shadow: 0 0 8px ${colors.primary};
    }
  }
  
  @media (max-width: 600px) {
    font-size: 0.8rem;
    padding: 1rem 0.5rem;
  }
`;

const ModalLink = styled.a`
  display: inline-block;
  background-color: transparent;
  color: ${colors.primary};
  padding: 0.75rem 1.5rem; /* Un peu plus de padding */
  border: 1px solid ${colors.primary};
  border-radius: 4px;
  text-decoration: none;
  font-weight: bold;
  transition: background-color 0.3s ease, color 0.3s ease, transform 0.2s ease, box-shadow 0.3s ease;
  box-shadow: 0 0 5px rgba(57, 255, 20, 0.5); /* Ombre verte plus subtile */
  text-shadow: 0 0 5px rgba(57, 255, 20, 0.5);
  margin-right: 1rem; /* Pour l'espacement avec le bouton Fermer */
  margin-bottom: 1rem; /* Garder la marge inférieure */

  &:hover {
    background-color: ${colors.primary};
    color: #0A0A0A; /* Couleur du fond de la modale pour le texte */
    transform: translateY(-2px);
    box-shadow: 0 0 12px rgba(57, 255, 20, 0.8); /* Ombre verte plus intense au survol */
    text-shadow: none; /* Pas besoin d'ombre de texte sur fond plein */
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  color: ${colors.primary};
  font-size: 2rem;
  cursor: pointer;
  line-height: 1;

  &:hover {
    color: #FF4136; /* Rouge pour l'effet de survol */
  }
`;

const BootOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: #000000;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  font-family: 'JetBrains Mono', monospace;
  color: ${colors.primary};
  font-size: 1.2rem;
  line-height: 1.8;
  text-shadow: 0 0 5px ${colors.primary};
  opacity: ${props => props.visible ? 1 : 0};
  pointer-events: ${props => props.visible ? 'all' : 'none'};
  transition: opacity 0.3s ease-out;
`;

const TerminalCursor = styled.span`
  display: inline-block;
  width: 0.6rem;
  height: 1.2rem;
  background: ${colors.primary};
  animation: ${blink} 1s step-end infinite;
  vertical-align: middle;
  margin-left: 0.3rem;
`;

const TerminalPrompt = styled.div`
  position: fixed;
  bottom: 1rem;
  left: 1.5rem;
  font-family: 'JetBrains Mono', monospace;
  color: ${colors.primary};
  display: flex;
  align-items: center;
  
  &::before {
    content: '>';
    margin-right: 0.5rem;
  }
`;

const TechTag = styled.span`
  display: inline-block;
  padding: 0.2rem 0.5rem;
  margin-top: 0.5rem;
  font-size: 0.7rem;
  background: rgba(57, 255, 20, 0.1);
  color: ${colors.primary};
  border: 1px solid ${colors.terminalBorder};
  border-radius: 4px;
  font-family: 'JetBrains Mono', monospace;
  position: absolute;
  bottom: 0.5rem;
  right: 0.5rem;
`;

const MatrixAnimation = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 150px; /* Augmenté de 100px à 150px */
  background: linear-gradient(to bottom, rgba(0,0,0,0.1), rgba(0,255,0,0.15));
  overflow: hidden;
  z-index: 0;
  pointer-events: none;
  opacity: 0.6; /* Augmenté de 0.3 à 0.6 */
`;

const MatrixColumn = styled.div`
  position: absolute;
  top: 0;
  width: 15px; /* Augmenté de 10px à 15px */
  height: 100%;
  font-family: 'JetBrains Mono', monospace;
  color: ${colors.primary};
  font-size: 14px; /* Taille de police augmentée */
  text-shadow: 0 0 8px ${colors.primary}; /* Effet glow renforcé */
  writing-mode: vertical-rl;
  text-orientation: mixed;
  animation: matrixFall ${props => props.duration || '3s'} linear infinite; /* Animation plus rapide */
  animation-delay: ${props => props.delay || '0s'};
  
  @keyframes matrixFall {
    0% { transform: translateY(-100%); opacity: 0; }
    5% { opacity: 1; }
    95% { opacity: 1; }
    100% { transform: translateY(100%); opacity: 0; }
  }
`;

function ProjectModal({ project, onClose }) {
  if (!project) {
    return null;
  }

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>{project.title}</ModalHeader>
        {project.imageUrl && <ModalImageWrapper>
          <ModalImage src={project.imageUrl} alt={`Aperçu de ${project.title}`} />
        </ModalImageWrapper>}
        <ModalDescription>{project.longDescription || project.description}</ModalDescription>
        <ModalLink href={project.link} target="_blank" rel="noopener noreferrer">Voir le projet</ModalLink>
        <CloseButton onClick={onClose}>&times;</CloseButton>
      </ModalContent>
    </ModalOverlay>
  );
}

function TerminalContent() {
  // Gestion de l'affichage progressif des sections et navigation
  const [activeSection, setActiveSection] = useState('projects');
  
  // Fonction pour ouvrir un projet dans un nouvel onglet
  const openProject = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };
  
  // Fonction pour changer de section active
  const navigateTo = (section) => {
    setActiveSection(section);
  };

  const [selectedProject, setSelectedProject] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isWidgetVisible, setIsWidgetVisible] = useState(true);

  const openModal = (project) => {
    setSelectedProject(project);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedProject(null);
  };

  return (
    <>
      <TerminalPanel>
        <div style={{ marginTop: '30px' }}></div>
        <Header>
          <SiteTitle>N4TH4N.DEV</SiteTitle>
          <Nav>
            <a 
              href="#projects" 
              className={activeSection === 'projects' ? 'active' : ''}
              onClick={(e) => { e.preventDefault(); navigateTo('projects'); }}
            >
              PROJETS
            </a>
            <a 
              href="#contact" 
              className={activeSection === 'contact' ? 'active' : ''}
              onClick={(e) => { e.preventDefault(); navigateTo('contact'); }}
            >
              CONTACT
            </a>
          </Nav>
        </Header>
        
        <div style={{ height: '50px' }} className="top-spacer"></div>
        
        <MainContent>
          <ScrollContainer>
            <SectionsContainer>
              {activeSection === 'projects' && (
                <Section>
                  <SectionTitle style={{ margin: '0 0 1rem 1rem', padding: 0 }}><BlinkingPrompt>&gt;</BlinkingPrompt><TypingEffect text={'PROJETS WEB'} /></SectionTitle>
                  <ProjectsGrid>
                    {projects.map(project => (
                      <ProjectCard key={project.id} onClick={() => openModal(project)}>
                        <h4>{project.title}</h4>
                        <p>{project.description}</p>
                        {project.technologies && project.technologies[0] && (
                          <TechTag>{project.technologies[0]}</TechTag>
                        )}
                      </ProjectCard>
                    ))}
                  </ProjectsGrid>
                  
                  <SectionTitle style={{ margin: '3rem 0 1rem 1rem', padding: 0 }}><BlinkingPrompt>&gt;</BlinkingPrompt><TypingEffect text={'PROJETS APP MOBILE'} /></SectionTitle>
                  
                  <ProjectsGrid>
                    {projectsApp.map(project => (
                      <ProjectCard key={project.id} onClick={() => openModal(project)}>
                        <h4>{project.title}</h4>
                        <p>{project.description}</p>
                        {project.technologies && project.technologies[0] && (
                          <TechTag>{project.technologies[0]}</TechTag>
                        )}
                      </ProjectCard>
                    ))}
                  </ProjectsGrid>
                </Section>
              )}
              
              {activeSection === 'contact' && (
                <Section>
                  <SectionTitle style={{ marginTop: '0', marginBottom: '2rem', marginLeft: '1rem' }}><BlinkingPrompt>&gt;</BlinkingPrompt><TypingEffect text={'CONTACT'} /></SectionTitle>
                  <SocialLinks>
                    <a href="mailto:x@n4th4n.dev" className="terminal-link">EMAIL</a>
                    <a href="https://www.github.com/n4th4ndev" target="_blank" rel="noopener noreferrer" className="terminal-link">GITHUB</a>
                    <a href="https://www.instagram.com/nathsbbn2" target="_blank" rel="noopener noreferrer" className="terminal-link">INSTAGRAM</a>
                  </SocialLinks>
                  
                  <FormWrapper> 
                    <form action="https://formsubmit.co/x@n4th4n.dev" method="POST" style={{ width: '100%' }}> 
                      <input type="hidden" name="_next" value="https://n4th4n.dev?message=sent" />
                      <input type="hidden" name="_subject" value="Nouveau message du site n4th4n.dev" />
                      <input type="hidden" name="_captcha" value="true" />
                      <input type="hidden" name="_turnstile" value="true" />
                      
                      <StyledInput 
                        type="text" 
                        name="name"
                        placeholder="Votre nom" 
                        required
                      />
                      
                      <StyledInput 
                        type="email" 
                        name="email"
                        placeholder="Votre email" 
                        required
                      />
                      
                      <StyledTextarea 
                        name="message"
                        placeholder="Votre message" 
                        rows="4" 
                        required
                      ></StyledTextarea>
                      
                      <div style={{ marginBottom: '1.5rem', width: '100%', display: 'flex', justifyContent: 'center' }}> 
                        <div className="cf-turnstile" data-sitekey="0x4AAAAAABe3n-ZuNChWlBjl" data-theme="dark"></div>
                      </div>
                      
                      <StyledButton 
                        type="submit"
                      >
                        ENVOYER
                      </StyledButton>
                    </form>
                  </FormWrapper>
                </Section>
              )}
              
              <TerminalFooter>
                <a href="https://github.com/n4th4ndev" target="_blank" rel="noopener noreferrer">GitHub</a>
                <span style={{ margin: '0 0.5rem' }}>|</span>
                <a href="https://www.instagram.com/nathsbbn2" target="_blank" rel="noopener noreferrer">Instagram</a>
  
              </TerminalFooter>


            </SectionsContainer>
          </ScrollContainer>
        </MainContent>
      </TerminalPanel>
      <CodeurWidgetContainer id="code-widget-container" visible={isWidgetVisible}>
        <WidgetToggleButton onClick={() => setIsWidgetVisible(!isWidgetVisible)}>
          {isWidgetVisible ? '−' : '+'}
        </WidgetToggleButton>
        <div data-layout="medium" data-style="dark" className="codeur-profile-widget"></div>
      </CodeurWidgetContainer>
      <TerminalPrompt>
        _<TerminalCursor />
      </TerminalPrompt>

      {isModalOpen && <ProjectModal project={selectedProject} onClose={closeModal} />}
    </>
  );
}

// Animation de la caméra sans tracking de souris
function CameraRig() {
  const cameraRef = useRef();
  
  useFrame(({ clock }) => {
    if (cameraRef.current) {
      // Légère rotation pour donner un effet de flottement, sans tracking de souris
      cameraRef.current.rotation.x = Math.sin(clock.getElapsedTime() * 0.1) * 0.02;
      cameraRef.current.rotation.y = Math.sin(clock.getElapsedTime() * 0.05) * 0.02;
    }
  });
  
  return (
    <PerspectiveCamera ref={cameraRef} makeDefault position={[0, 0, 10]} fov={50} />
  );
}

// Grille de fond vide
function Grid() {
  return null;
}

// Composant principal
// Fonction vide pour les particules de fond
function BackgroundParticles() {
  return null;
}

// Composant principal
const scanInAnimation = keyframes`
  0% {
    clip-path: inset(50% 50% 50% 50%); /* Réduit à un point au centre */
    opacity: 0;
  }
  70% {
    opacity: 1; /* Devient opaque assez vite */
  }
  100% {
    clip-path: inset(0% 0% 0% 0%);   /* Taille normale */
    opacity: 1;
  }
`;

const AnimatedContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: #000000; /* Fond noir principal pour la scène 3D */
  z-index: 0; /* Assure que le canvas est derrière le terminal */
  margin: 0;
  padding: 0;
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  opacity: 0; /* Initialement invisible */
  clip-path: inset(50% 50% 50% 50%); /* État initial de l'animation */
  animation: ${scanInAnimation} 1.2s cubic-bezier(0.25, 1, 0.5, 1) forwards;
`;

function Terminal3D() {
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://api.codeur.com/widgets/profile.js?k=dj6O19AuCIUAtFx9';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const [bootComplete, setBootComplete] = useState(false);
  const [bootMessages, setBootMessages] = useState([
    '> Initializing system...',
    '> Loading components...',
    '> Starting terminal interface...',
    '> System ready'
  ]);
  const [currentMessage, setCurrentMessage] = useState(0);

  useEffect(() => {
    if (!bootComplete) {
      const timer = setInterval(() => {
        setCurrentMessage(prev => {
          if (prev >= bootMessages.length - 1) {
            clearInterval(timer);
            setTimeout(() => setBootComplete(true), 160);
            return prev;
          }
          return prev + 1;
        });
      }, 160);
      return () => clearInterval(timer);
    }
  }, [bootComplete]);

  return (
    <>
      {!bootComplete && (
        <BootOverlay visible={true}>
          {bootMessages.slice(0, currentMessage + 1).map((msg, i) => (
            <div key={i}>{msg}</div>
          ))}
          {!bootComplete && <TerminalCursor />}
        </BootOverlay>
      )}

      <GlobalTerminalEffects />
      <div className="noise-bg" />
      <TerminalContent style={{ 
        width: '100%', 
        padding: 0, 
        margin: 0,
        display: bootComplete ? 'block' : 'none' 
      }} />

      {bootComplete && (
        <TerminalPrompt>
          _<TerminalCursor />
        </TerminalPrompt>
      )}
      <MatrixAnimation>
        {[...Array(80).keys()].map(i => (
          <MatrixColumn 
            key={i} 
            style={{ left: `${(i * 15)}px` }}
            duration={`${Math.random() * 2 + 2}s`}
            delay={`${Math.random() * 2}s`}
          >
            {Array.from({ length: 30 }, (_, j) => (
              <span key={j} style={{ 
                opacity: Math.random() * 0.5 + 0.5,
                transform: `translateY(${j * 15}px)`
              }}>
                {Math.random() < 0.3 ? '1' : '0'}
              </span>
            ))}
          </MatrixColumn>
        ))}
      </MatrixAnimation>
    </>
  );
};

export default Terminal3D;
